import React, { useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { translate } from '../../Services/translation';
import { numberFormat } from '../../Services/formatting';
import convertUnit from '../../Services/conversions';
import { map } from '../../Services/utilities';
import PanelCoilFoiling from './PanelCoilFoiling';
import PanelCoilPacking from './PanelCoilPacking';
import { setEmptyCoilDetails as addCoil, setCoilDetails, deleteCoilDetails as deleteCoil } from '../../Actions/Configurator.action';
import { googleAnalyticsProductConfigurator } from '../../Services/googleAnalytics';

import PlusIcon from '../../../images/Plus.svg';
import TrashCan from '../../../images/trashcan.svg';

const PanelCoilBase = () => {
  const dispatch = useDispatch();
  const configurator = useSelector((state) => state.configurator);
  const { rules, selections, quantitySelector } = configurator;
  const { baseUnit, quantityUnits } = quantitySelector;
  const { coilInfo } = rules;
  const { foilingOptions, packingOptions } = coilInfo;
  const kgSuffix = ' kg';

  useEffect(() => {
    if (selections.coilDetails && selections.coilDetails.length == 0) {
      coilAdd();
    }
  }, []);

  const coilAdd = () => {
    dispatch(addCoil());
  };

  const handleInputChange = (id, field, value) => {
    dispatch(setCoilDetails(id, field, Number(value)));
    googleAnalyticsProductConfigurator(field, value, configurator.id);
  };

  const handleDelete = (id) => {
    if (selections.coilDetails.length === 1) {
      dispatch(deleteCoil(id));
      dispatch(addCoil());
    } else {
      dispatch(deleteCoil(id));
    }
  };

  const calculateWeight = (row) => {
    const pcsUnit = quantityUnits.find((u) => u.unit === 'pcs'),
      kgUnit = quantityUnits.find((u) => u.unit === 'kg');

    let baseQuantity = convertUnit(row.quantity, pcsUnit, baseUnit),
      totalWeight = convertUnit(baseQuantity, baseUnit, kgUnit);

    if (isCoilLengthValid(row.length)) {
      totalWeight = map(row.length, 0, rules.length, 0, totalWeight);
    }
    return totalWeight;
  };

  const isCoilLengthValid = (length) => {
    return length >= rules.minCutLength && length <= rules.maxCutLength;
  };

  const getRowWeight = (accumulator, row) => {
    return accumulator + calculateWeight(row);
  };

  const getTotalWeight = () => {
    return selections.coilDetails.reduce(getRowWeight, 0);
  };

  return (
    <Fragment>
      <div className='configurator-box'>
        <section className='header-section'>
          <span>{translate('cutting.coil.title')}</span>
          <details className='config-details config-details--right-align config-details--link'>
            <summary>
              <span>{translate('cutting.coil.showtoleranser')}</span>
              <img className='config-details__icon' src='/ui/images/arrow-down.svg' />
            </summary>
            <div className='config-details__content'>
              <img src={rules.tolerance} style={{ minWidth: '400px' }} />
            </div>
          </details>
        </section>

        <div className='coil-table'>
          <table>
            <thead>
              <tr>
                <th className='coil-table__quantity-cell'>
                  {translate('cutting.coil.pieces')}
                </th>
                <th className='coil-table__length-cell'>
                  {translate('cutting.coil.length')}
                </th>
                <th className='coil-table__foiling-cell'>
                  {translate('cutting.coil.foiling')}
                </th>
                <th className='coil-table__packing-cell'>
                  {translate('cutting.coil.packing')}
                </th>
                <th className='coil-table__max-coils-cell'>
                  {translate('cutting.coil.maxcoils')}
                </th>
                <th className='coil-table__weight-cell'>
                  {translate('cutting.coil.rowweight')}
                </th>
                <th className='coil-table__delete-cell'>
                  {/* Delete column */}
                </th>
              </tr>
            </thead>
            <tbody>
              {selections.coilDetails && selections.coilDetails.map((row) => (
                <tr key={row.id}>
                  <td className='coil-table__quantity-cell'>
                    <input className='coil-table__input' value={row.quantity} type='number'
                      onChange={(e) => handleInputChange(row.id, 'quantity', e.target.value)}
                      onBlur={(e) => handleInputChange(row.id, 'quantity', e.target.value)} />
                  </td>
                  <td className='coil-table__length-cell'>
                    <input className='coil-table__input' min={rules.minCutLength} max={rules.maxCutLength} type='number'
                      value={row.length === 0 ? '' : row.length} placeholder={rules.minCutLength + '-' + rules.maxCutLength}
                      onChange={(e) => handleInputChange(row.id, 'length', e.target.value)}
                      onBlur={(e) => handleInputChange(row.id, 'length', e.target.value)} />
                  </td>
                  <td className='coil-table__foiling-cell'>
                    <details className='config-details config-details--bordered' name='coilGroup'                      >
                      <summary title={foilingOptions[row.foilingCoils]}>
                        <span>
                          {row.foilingCoils ? foilingOptions[row.foilingCoils] : translate('not.selected')}
                        </span>
                        <img className='config-details__icon' src='/ui/images/arrow-down.svg'
                        />
                      </summary>
                      <div className='config-details__content'>
                        <PanelCoilFoiling id={row.id}></PanelCoilFoiling>
                      </div>
                    </details>
                  </td>
                  <td className='coil-table__packing-cell'>
                    <details className='config-details config-details--bordered config-details--right-align' name='coilGroup'                      >
                      <summary title={packingOptions[row.packingCoils]}>
                        <span>
                          {row.packingCoils ? packingOptions[row.packingCoils] : translate('not.selected')}
                        </span>
                        <img className='config-details__icon' src='/ui/images/arrow-down.svg' />
                      </summary>
                      <div className='config-details__content'>
                        <PanelCoilPacking id={row.id}></PanelCoilPacking>
                      </div>
                    </details>
                  </td>
                  <td className='coil-table__max-coils-cell'>
                    <details className='config-details config-details--bordered config-details--right-align' name='coilGroup'                      >
                      <summary>
                        <span>
                          {row.maxHeightCoils > 0 && row.maxWeightCoils > 0 ? row.maxHeightCoils + ' : ' + row.maxWeightCoils : translate('not.selected')}
                        </span>
                        <img className='config-details__icon' src='/ui/images/arrow-down.svg' />
                      </summary>
                      <div className='config-details__content'>
                        <div className='accordion-panel__panel_header'></div>
                        <div className='accordion-panel__panel_content'>
                          <div className='configurator__coil_max_bundle'>
                            <div className='coil_max_bundle_body coil_max_bundle_body--no-flex'>
                              <div className='max_bundle-input'>
                                <label className='max_bundle-label'>
                                  <span>
                                    {translate('cutting.coil.maxheightcoils')}
                                  </span>
                                </label>
                                <input type='number' className='coil-table__input' value={row.maxHeightCoils}
                                  onChange={(e) => handleInputChange(row.id, 'maxHeightCoils', e.target.value)}
                                  onBlur={(e) => handleInputChange(row.id, 'maxHeightCoils', e.target.value)}
                                />
                              </div>
                              <div className='max_bundle-input'>
                                <label className='max_bundle-label'>
                                  <span>
                                    {translate('cutting.coil.maxweightcoils')}
                                  </span>
                                </label>
                                <input type='number' className='coil-table__input' value={row.maxWeightCoils}
                                  onChange={(e) => handleInputChange(row.id, 'maxWeightCoils', e.target.value)}
                                  onBlur={(e) => handleInputChange(row.id, 'maxWeightCoils', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </details>
                  </td>
                  <td className='coil-table__weight-cell'>
                    {numberFormat.format(getRowWeight(0, row)) + kgSuffix}
                  </td>
                  <td className='coil-table__delete-cell'>
                    <a tabIndex={0} onClick={() => handleDelete(row.id)}>
                      <img src={TrashCan} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>
                  <a tabIndex={0} onClick={coilAdd}>
                    <img src={PlusIcon} /> {translate('cutting.coil..add')}
                  </a>
                </td>
                <td colSpan={3}>
                  <span>
                    {translate('cutting.coil.total') + ' ' + numberFormat.format(getTotalWeight()) + kgSuffix}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </Fragment>
  );
};

export default PanelCoilBase;
